/**
 * **The revenue entry grid, as a spreadsheet — RV-5, `docs/EDITS-2026-09-24.md` §6.**
 *
 * Jesse backfills revenue by hand, so this is a data-entry surface and it is
 * keyboard-first. It replaces a grid of one `<input>` per cell — where the only
 * shortcuts were ↑/↓ and a block paste — with the spreadsheet conventions people
 * already have in their fingers, following the blueprint's DataGrid contract:
 *
 * | Key | Not editing | Editing |
 * |---|---|---|
 * | Arrows | move (Shift extends; Ctrl/⌘ jumps to the edge) | move the caret, or commit and move if the edit began by typing |
 * | Enter · F2 · double-click | start editing | Enter commits and moves **down**, Shift+Enter up |
 * | A printable character | replace the cell and start editing | types |
 * | Tab · Shift+Tab | next / previous cell | commit and move |
 * | Esc | clear the copy marquee | cancel |
 * | Shift+click · drag | extend the selection | — |
 * | Home · End (Ctrl/⌘: grid) · PageUp · PageDown | move | — |
 * | Ctrl/⌘+A | select all | — |
 * | Ctrl/⌘+C · X · V | copy · cut · paste, tab-separated — round-trips with Google Sheets | native |
 * | Delete · Backspace | clear the selection | native |
 * | Ctrl/⌘+D · R | fill down · fill right | — |
 * | Ctrl/⌘+Enter | — | commit into every selected cell |
 * | Ctrl/⌘+Z · Ctrl+Y · ⌘+Shift+Z | undo · redo unsaved edits | native |
 *
 * 🔴 **Enter moves down now, reversing the 8 Sep choice.** It moved *right*,
 * because the unit of work was "a day across the channels". The 24 Sep meeting
 * asked for Google Sheets behaviour, which is what Jesse's hands know; Tab is
 * the across-the-row key.
 *
 * All logic is in `lib/sheet.ts`; this file only maps events onto it. Rows are
 * memoised, so moving the selection re-renders the rows it touches rather than
 * every cell of a two-year range.
 */
import { memo, useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

import { headCell } from './DataTable'
import { Tag } from './ui'
import type { RevenueGridChannel } from '../lib/api'
import { formatDate } from '../lib/dates'
import { formatMoney, parseMoney, sanitizeMoneyInput } from '../lib/money'
import {
  type Cell,
  type Edits,
  type Grid,
  type History,
  type Move,
  type Outcome,
  type Rect,
  clear,
  copyText,
  fillDown,
  fillRight,
  fillSelection,
  move,
  paste,
  rectOf,
  record,
  redo,
  summarise,
  tabFrom,
  undo,
} from '../lib/sheet'

const numberStyle = { fontVariantNumeric: 'tabular-nums' } as const
const MIN_CELL_WIDTH = '5.5rem'

// 13 channels never fit a laptop, so the grid scrolls horizontally. The two
// columns needed *while* scrolling — the date and the day total — are pinned,
// as are the header and the totals row. Backgrounds are opaque tokens because a
// sticky cell sits over the scrolling ones. Moved here from `pages/Revenue.tsx`.
const stickyLeft = { position: 'sticky', left: 0, zIndex: 2, background: 'var(--bg-surface)' } as const
const stickyRight = {
  position: 'sticky',
  right: 0,
  zIndex: 2,
  background: 'var(--bg-surface)',
  borderLeft: '1px solid var(--border)',
} as const
const stickyTop = { position: 'sticky', top: 0, zIndex: 3, background: 'var(--bg-surface)' } as const

const bodyCell = {
  padding: 'var(--space-1) var(--space-2)',
  borderBottom: '1px solid var(--border-subtle, var(--border))',
  color: 'var(--text-primary)',
  whiteSpace: 'nowrap',
} as const

const footCell = {
  padding: 'var(--space-2)',
  borderTop: '1px solid var(--border)',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  position: 'sticky',
  bottom: 0,
  zIndex: 3,
  background: 'var(--bg-surface)',
} as const

export interface SheetProps {
  channels: RevenueGridChannel[]
  days: string[]
  today: string
  /** The stored figure for a cell, formatted; `''` where nothing is recorded. */
  storedAt: (profileId: string, day: string) => string
  edits: Edits
  /** Every change goes through here, so the page can keep history and the count. */
  onEdits: (next: Edits) => void
  onError: (message: string | null) => void
  history: History
  onHistory: (next: History) => void
  headerFor: (channel: RevenueGridChannel, index: number) => ReactNode
  editable: boolean
}

function cellKey(profileId: string, day: string): string {
  return `${profileId}|${day}`
}

type Editing = { value: string; typed: boolean } | null

interface LiveHandlers {
  editValue: (value: string) => void
  editKey: (event: React.KeyboardEvent<HTMLInputElement>, value: string) => void
  editPaste: (event: React.ClipboardEvent<HTMLInputElement>) => void
  down: (row: number, col: number, shift: boolean) => void
  enter: (row: number, col: number) => void
  double: (row: number, col: number) => void
}

export function RevenueSheet(props: SheetProps) {
  const { channels, days, today, storedAt, edits, onEdits, onError, history, onHistory, editable } =
    props
  const shape = { rows: days.length, cols: channels.length }

  const [active, setActive] = useState<Cell>({ row: 0, col: 0 })
  const [anchor, setAnchor] = useState<Cell>({ row: 0, col: 0 })
  const [editing, setEditing] = useState<Editing>(null)
  const [copied, setCopied] = useState<Rect | null>(null)
  const dragging = useRef(false)
  const root = useRef<HTMLDivElement>(null)
  const live = useRef<LiveHandlers>(null as unknown as LiveHandlers)

  const rect = rectOf(anchor, active)

  const valueAt = useCallback(
    (row: number, col: number): string => {
      const day = days[row]
      const channel = channels[col]
      if (!day || !channel) return ''
      const key = cellKey(channel.profile_id, day)
      return edits[key] ?? storedAt(channel.profile_id, day)
    },
    [channels, days, edits, storedAt],
  )

  const grid: Grid = {
    shape,
    valueAt,
    keyAt: (row, col) => cellKey(channels[col]!.profile_id, days[row]!),
    // RV-7: a future day is shown and never writable, here and on the server.
    writable: (row) => editable && (days[row] ?? '') <= today,
  }

  // A range change can shrink the grid under the cursor.
  useEffect(() => {
    setActive((cell) => ({
      row: Math.min(cell.row, Math.max(0, shape.rows - 1)),
      col: Math.min(cell.col, Math.max(0, shape.cols - 1)),
    }))
    setAnchor((cell) => ({
      row: Math.min(cell.row, Math.max(0, shape.rows - 1)),
      col: Math.min(cell.col, Math.max(0, shape.cols - 1)),
    }))
  }, [shape.rows, shape.cols])

  useEffect(() => {
    const up = () => {
      dragging.current = false
    }
    window.addEventListener('mouseup', up)
    return () => window.removeEventListener('mouseup', up)
  }, [])

  // Keep the active cell on screen as the keyboard moves it.
  useEffect(() => {
    root.current
      ?.querySelector(`[data-sheet-cell="${active.row}:${active.col}"]`)
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [active])

  const apply = (outcome: Outcome) => {
    if (outcome.error) {
      onError(outcome.error)
      return false
    }
    onError(null)
    if (!Object.keys(outcome.edits).length) return true
    onHistory(record(history, edits))
    onEdits({ ...edits, ...outcome.edits })
    return true
  }

  const focusGrid = () => root.current?.focus({ preventScroll: true })

  const goTo = (cell: Cell, extend = false) => {
    setActive(cell)
    if (!extend) setAnchor(cell)
  }

  const commit = (
    value: string,
    then: 'down' | 'up' | 'left' | 'right' | 'next' | 'previous' | 'stay' | 'fill',
  ) => {
    const clean = value.trim() === '' ? '' : sanitizeMoneyInput(value, '')
    if (value.trim() !== '' && clean === '') {
      onError(`“${value}” Is Not a Number.`)
      return
    }
    const ok =
      then === 'fill'
        ? apply(fillSelection(grid, rect, clean, edits))
        : apply(fillSelection(grid, { top: active.row, bottom: active.row, left: active.col, right: active.col }, clean, edits))
    if (!ok) return
    setEditing(null)
    if (then === 'down' || then === 'up' || then === 'left' || then === 'right') {
      goTo(move(active, then, shape))
    }
    if (then === 'next' || then === 'previous') {
      const next = tabFrom(active, then === 'previous', shape)
      if (next) goTo(next)
    }
    focusGrid()
  }

  const startEditing = (value: string, typed: boolean) => {
    if (!grid.writable(active.row, active.col)) {
      onError('Future Dates Cannot Be Entered.')
      return
    }
    setEditing({ value, typed })
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (editing) return // the editor handles its own keys
    const mod = event.ctrlKey || event.metaKey
    const key = event.key

    const nav: Record<string, Move> = mod
      ? { ArrowUp: 'edgeUp', ArrowDown: 'edgeDown', ArrowLeft: 'edgeLeft', ArrowRight: 'edgeRight', Home: 'gridStart', End: 'gridEnd' }
      : { ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right', Home: 'rowStart', End: 'rowEnd', PageUp: 'pageUp', PageDown: 'pageDown' }
    const how = nav[key]
    if (how) {
      event.preventDefault()
      goTo(move(active, how, shape), event.shiftKey)
      return
    }

    if (key === 'Tab') {
      const next = tabFrom(active, event.shiftKey, shape)
      // Past either end, Tab leaves the grid, as it should.
      if (next) {
        event.preventDefault()
        goTo(next)
      }
      return
    }
    if (key === 'Enter' || key === 'F2') {
      event.preventDefault()
      startEditing(valueAt(active.row, active.col), false)
      return
    }
    if (key === 'Escape') {
      setCopied(null)
      return
    }
    if (key === 'Delete' || key === 'Backspace') {
      event.preventDefault()
      apply(clear(grid, rect, edits))
      return
    }
    if (mod && key.toLowerCase() === 'a') {
      event.preventDefault()
      setAnchor({ row: 0, col: 0 })
      setActive({ row: shape.rows - 1, col: shape.cols - 1 })
      return
    }
    if (mod && key.toLowerCase() === 'd') {
      event.preventDefault()
      apply(fillDown(grid, rect, edits))
      return
    }
    if (mod && key.toLowerCase() === 'r') {
      event.preventDefault()
      apply(fillRight(grid, rect, edits))
      return
    }
    if (mod && (key.toLowerCase() === 'y' || (key.toLowerCase() === 'z' && event.shiftKey))) {
      event.preventDefault()
      const step = redo(history, edits)
      if (step) {
        onHistory(step.history)
        onEdits(step.edits)
      }
      return
    }
    if (mod && key.toLowerCase() === 'z') {
      event.preventDefault()
      const step = undo(history, edits)
      if (step) {
        onHistory(step.history)
        onEdits(step.edits)
      }
      return
    }
    // Typing replaces the cell — the spreadsheet convention.
    if (!mod && !event.altKey && key.length === 1 && /[\d.,$\-]/.test(key)) {
      event.preventDefault()
      startEditing(key, true)
    }
  }

  const onCopy = (event: React.ClipboardEvent) => {
    if (editing) return
    event.preventDefault()
    event.clipboardData.setData('text/plain', copyText(grid, rect))
    setCopied(rect)
  }

  const onCut = (event: React.ClipboardEvent) => {
    if (editing) return
    onCopy(event)
    apply(clear(grid, rect, edits))
  }

  const pasteText = (text: string) => {
    apply(
      paste(grid, rect, text, edits, {
        profileIds: channels.map((channel) => channel.profile_id),
        days,
      }),
    )
    setCopied(null)
  }

  const onPaste = (event: React.ClipboardEvent) => {
    if (editing) return
    event.preventDefault()
    pasteText(event.clipboardData.getData('text/plain'))
  }

  const summary = summarise(grid, rect, parseMoney)
  const multi = rect.top !== rect.bottom || rect.left !== rect.right

  const rowValues = (row: number) => channels.map((_, col) => valueAt(row, col))

  /**
   * **Every row handler reads the latest state through this ref.** Rows are
   * memoised and deliberately skip re-rendering when only their handlers would
   * change, so a handler that closed over state directly would act on the
   * selection or edit of whenever that row last drew. Assigned each render;
   * the wrappers passed to rows only ever dereference it.
   */
  live.current = {
    editValue: (value: string) =>
      setEditing((current) => (current ? { ...current, value } : current)),
    editKey: (event: React.KeyboardEvent<HTMLInputElement>, value: string) => {
      const mod = event.ctrlKey || event.metaKey
      if (event.key === 'Escape') {
        event.preventDefault()
        setEditing(null)
        focusGrid()
      } else if (event.key === 'Enter') {
        event.preventDefault()
        commit(value, mod ? 'fill' : event.shiftKey ? 'up' : 'down')
      } else if (event.key === 'Tab') {
        event.preventDefault()
        commit(value, event.shiftKey ? 'previous' : 'next')
      } else if (
        editing?.typed &&
        ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)
      ) {
        // An edit begun by typing is "enter mode": arrows commit and move, as in
        // Sheets. One begun with Enter/F2 is "edit mode", where they move the
        // caret to fix a typo.
        event.preventDefault()
        const direction = (
          { ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right' } as const
        )[event.key as 'ArrowUp']
        commit(value, direction)
      }
    },
    editPaste: (event: React.ClipboardEvent<HTMLInputElement>) => {
      const text = event.clipboardData.getData('text/plain')
      // A block pasted mid-edit is a grid paste, not a string.
      if (!/[\t\n]/.test(text.replace(/\r?\n$/, ''))) return
      event.preventDefault()
      setEditing(null)
      pasteText(text)
      focusGrid()
    },
    down: (row: number, col: number, shift: boolean) => {
      if (editing) commit(editing.value, 'stay')
      dragging.current = true
      goTo({ row, col }, shift)
      focusGrid()
    },
    enter: (row: number, col: number) => {
      if (dragging.current) setActive({ row, col })
    },
    double: (row: number, col: number) => {
      goTo({ row, col })
      if (grid.writable(row, col)) setEditing({ value: valueAt(row, col), typed: false })
    },
  }

  return (
    <>
      <div
        ref={root}
        role="grid"
        aria-label="Revenue Entry Grid"
        aria-rowcount={days.length + 1}
        aria-colcount={channels.length + 2}
        aria-activedescendant={`sheet-${active.row}-${active.col}`}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onCopy={onCopy}
        onCut={onCut}
        onPaste={onPaste}
        className="overflow-auto revenue-sheet"
        style={{ maxHeight: 'calc(100vh - 16rem)', outline: 'none' }}
      >
        <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...headCell, ...stickyTop, ...stickyLeft, zIndex: 4 }} className="text-left">
                Date
              </th>
              {channels.map((channel, index) => (
                <th
                  key={channel.profile_id}
                  style={{
                    ...headCell,
                    ...stickyTop,
                    minWidth: MIN_CELL_WIDTH,
                    borderLeft:
                      index > 0 && channel.language_group !== channels[index - 1]?.language_group
                        ? '1px solid var(--border-strong, var(--border))'
                        : undefined,
                  }}
                  className="text-right"
                >
                  {props.headerFor(channel, index)}
                </th>
              ))}
              <th style={{ ...headCell, ...stickyTop, ...stickyRight, zIndex: 4 }} className="text-right">
                Day Total
              </th>
            </tr>
          </thead>
          <tbody>
            {days.map((day, row) => {
              const selected = row >= rect.top && row <= rect.bottom
              const marquee = copied && row >= copied.top && row <= copied.bottom ? copied : null
              return (
                <SheetRow
                  key={day}
                  day={day}
                  row={row}
                  today={today}
                  values={rowValues(row)}
                  dirty={channels.map((channel) => cellKey(channel.profile_id, day) in edits)}
                  selLeft={selected ? rect.left : -1}
                  selRight={selected ? rect.right : -1}
                  marqueeLeft={marquee ? marquee.left : -1}
                  marqueeRight={marquee ? marquee.right : -1}
                  activeCol={active.row === row ? active.col : -1}
                  editing={active.row === row ? editing : null}
                  onEditValue={(value) => live.current.editValue(value)}
                  onEditKey={(event, value) => live.current.editKey(event, value)}
                  onEditPaste={(event) => live.current.editPaste(event)}
                  onDown={(col, shift) => live.current.down(row, col, shift)}
                  onEnter={(col) => live.current.enter(row, col)}
                  onDouble={(col) => live.current.double(row, col)}
                />
              )
            })}
          </tbody>
          <tfoot>
            <tr>
              <td style={{ ...footCell, ...stickyLeft, zIndex: 4, color: 'var(--text-label)' }}>Total</td>
              {channels.map((channel, col) => {
                let total = 0
                let any = false
                for (let row = 0; row < days.length; row += 1) {
                  const value = parseMoney(valueAt(row, col))
                  if (value !== null) {
                    total += value
                    any = true
                  }
                }
                return (
                  <td key={channel.profile_id} style={{ ...footCell, ...numberStyle }} className="text-right">
                    {any ? formatMoney(total) : <span style={{ color: 'var(--text-label)' }}>—</span>}
                  </td>
                )
              })}
              <td style={{ ...footCell, ...numberStyle, ...stickyRight, zIndex: 4, fontWeight: 600 }} className="text-right">
                {formatMoney(
                  days.reduce(
                    (sum, _day, row) =>
                      sum + channels.reduce((inner, _c, col) => inner + (parseMoney(valueAt(row, col)) ?? 0), 0),
                    0,
                  ),
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* The selection summary, as in Sheets. Blanks add nothing rather than 0. */}
      <p className="mt-2 text-xs" style={{ color: 'var(--text-label)' }} aria-live="polite">
        {multi || summary.count ? (
          <>
            Sum <strong style={numberStyle}>{formatMoney(summary.sum)}</strong> · Average{' '}
            <strong style={numberStyle}>
              {summary.average === null ? '—' : formatMoney(Number(summary.average.toFixed(2)))}
            </strong>{' '}
            · Count <strong style={numberStyle}>{summary.count}</strong>
          </>
        ) : (
          'Arrows Move · Type or Enter to Edit · Ctrl/⌘+C, V to Copy and Paste · Ctrl/⌘+D to Fill Down · Ctrl/⌘+Z to Undo'
        )}
      </p>
    </>
  )
}

interface RowProps {
  day: string
  row: number
  today: string
  values: string[]
  dirty: boolean[]
  selLeft: number
  selRight: number
  marqueeLeft: number
  marqueeRight: number
  activeCol: number
  editing: Editing
  onEditValue: (value: string) => void
  onEditKey: (event: React.KeyboardEvent<HTMLInputElement>, value: string) => void
  onEditPaste: (event: React.ClipboardEvent<HTMLInputElement>) => void
  onDown: (col: number, shift: boolean) => void
  onEnter: (col: number) => void
  onDouble: (col: number) => void
}

function sameArray<T>(a: T[], b: T[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index])
}

/**
 * One date's row. Re-renders only when what it draws changes — its values, its
 * slice of the selection, or its editor — never because a *different* row's
 * cell became active. The handlers are deliberately left out of the comparison:
 * they close over the parent's state but are only ever called, never drawn.
 */
const SheetRow = memo(
  function SheetRow(props: RowProps) {
    const { day, row, today, values, dirty, activeCol, editing } = props
    const future = day > today
    const isToday = day === today
    const total = values.reduce((sum, value) => sum + (parseMoney(value) ?? 0), 0)
    const recorded = values.some((value) => parseMoney(value) !== null)

    return (
      <tr className="revenue-row">
        <td
          style={{
            ...bodyCell,
            ...numberStyle,
            ...stickyLeft,
            color: 'var(--text-label)',
            borderTop: isToday ? '2px solid var(--border-strong, var(--border))' : undefined,
          }}
        >
          {formatDate(day)}
          {isToday && (
            <span className="ml-2">
              <Tag tone="neutral">Today</Tag>
            </span>
          )}
        </td>
        {values.map((value, col) => {
          const isActive = col === activeCol
          const isSelected = col >= props.selLeft && col <= props.selRight
          const inMarquee = col >= props.marqueeLeft && col <= props.marqueeRight
          return (
            <td
              key={col}
              id={`sheet-${row}-${col}`}
              role="gridcell"
              aria-selected={isSelected}
              aria-readonly={future || undefined}
              data-sheet-cell={`${row}:${col}`}
              onMouseDown={(event) => {
                if (isActive && editing) return
                event.preventDefault()
                props.onDown(col, event.shiftKey)
              }}
              onMouseEnter={() => props.onEnter(col)}
              onDoubleClick={() => props.onDouble(col)}
              className="text-right"
              style={{
                ...bodyCell,
                ...numberStyle,
                minWidth: MIN_CELL_WIDTH,
                cursor: future ? 'not-allowed' : 'cell',
                userSelect: 'none',
                color: future ? 'var(--text-label)' : 'var(--text-primary)',
                // `--brand-tint`, never `--mint-100`: the raw swatch stays pale
                // in dark mode, where the text is also pale — edits vanished.
                background: dirty[col]
                  ? 'var(--brand-tint)'
                  : isSelected
                    ? 'var(--data-1-fill)'
                    : future
                      ? 'var(--bg-page)'
                      : undefined,
                outline: isActive
                  ? '2px solid var(--focus-ring)'
                  : inMarquee
                    ? '1px dashed var(--focus-ring)'
                    : undefined,
                outlineOffset: -2,
              }}
              title={future ? 'Future Dates Cannot Be Entered' : undefined}
            >
              {isActive && editing ? (
                <input
                  autoFocus
                  value={editing.value}
                  inputMode="decimal"
                  aria-label={`Edit ${formatDate(day)}`}
                  onChange={(event) =>
                    props.onEditValue(sanitizeMoneyInput(event.target.value, editing.value))
                  }
                  onKeyDown={(event) => props.onEditKey(event, event.currentTarget.value)}
                  onPaste={props.onEditPaste}
                  className="w-full px-1 text-right"
                  style={{
                    ...numberStyle,
                    border: 'none',
                    outline: 'none',
                    background: 'var(--bg-surface)',
                    color: 'var(--text-primary)',
                  }}
                  onFocus={(event) => {
                    // Typed-in edits keep the caret after the first character;
                    // Enter/F2 edits select the whole value, as Sheets does.
                    if (!editing.typed) event.currentTarget.select()
                  }}
                />
              ) : value === '' ? (
                ''
              ) : (
                formatMoney(parseMoney(value))
              )}
            </td>
          )
        })}
        <td style={{ ...bodyCell, ...numberStyle, ...stickyRight, fontWeight: 500 }} className="text-right">
          {recorded ? formatMoney(total) : <span style={{ color: 'var(--text-label)' }}>—</span>}
        </td>
      </tr>
    )
  },
  (a, b) =>
    a.day === b.day &&
    a.row === b.row &&
    a.today === b.today &&
    a.selLeft === b.selLeft &&
    a.selRight === b.selRight &&
    a.marqueeLeft === b.marqueeLeft &&
    a.marqueeRight === b.marqueeRight &&
    a.activeCol === b.activeCol &&
    a.editing === b.editing &&
    sameArray(a.values, b.values) &&
    sameArray(a.dirty, b.dirty),
)
